import React, { useState, useRef } from 'react';
import { INSTITUTION_TYPES } from '../data/institutionPresets';
import { db, storage } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { SchoolSettings } from '../types';
import type { PlanId } from '../services/pricingCore';
import { discountSummary, usePricing } from '../services/billingClient';
import PlanCards from './billing/PlanCards';
import { Spinner } from './common/UIComponents';

interface Props {
  open: boolean;
  onClose: () => void;
  schoolSettings?: SchoolSettings;
}

type Step = 1 | 2 | 3 | 4;

interface Form {
  schoolName: string;
  institutionType: string;
  address: string;
  city: string;
  country: string;
  contactName: string;
  email: string;
  phone: string;
  estimatedStudents: string;
  currentChallenges: string;
  interestedPlan: PlanId;
  referralCode: string;
}

const EMPTY: Form = {
  schoolName: '', institutionType: 'K-12 School', address: '', city: '', country: 'Nigeria',
  contactName: '', email: '', phone: '', estimatedStudents: '', currentChallenges: '',
  interestedPlan: 'std', referralCode: '',
};

const FIELD = ({ label, value, onChange, placeholder, type = 'text', required = true }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; required?: boolean;
}) => (
  <div>
    <label className="block text-xs font-bold text-slate-400 mb-1.5">
      {label}{required && <span className="text-rose-400 ml-0.5"> *</span>}
    </label>
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
    />
  </div>
);

const SELECT = ({ label, value, onChange, children }: {
  label: string; value: string; onChange: (v: string) => void; children: React.ReactNode;
}) => (
  <div>
    <label className="block text-xs font-bold text-slate-400 mb-1.5">{label}<span className="text-rose-400 ml-0.5"> *</span></label>
    <select
      value={value} onChange={e => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
    >
      {children}
    </select>
  </div>
);

export const SchoolPartnershipModal: React.FC<Props> = ({ open, onClose }) => {
  const [step, setStep] = useState<Step>(1);
  const [form, setForm] = useState<Form>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const pricing = usePricing();

  if (!open) return null;

  const set = (k: keyof Form, v: string) => setForm(f => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return !!(form.schoolName && form.institutionType && form.city && form.country);
    if (step === 2) return !!(form.contactName && form.email && form.phone);
    if (step === 3) return !!(form.estimatedStudents && form.currentChallenges.trim().length > 10);
    return true;
  };

  const reset = () => {
    setStep(1); setForm(EMPTY); setSubmitted(false); setError('');
    setLogoFile(null); setLogoPreview('');
    onClose();
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Logo must be under 2 MB'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const docRef = await addDoc(collection(db, 'schoolPartnerApplications'), {
        ...form,
        email: form.email.toLowerCase().trim(),
        referralCode: form.referralCode.toLowerCase().trim(),
        status: 'pending',
        submittedAt: Date.now(),
      });
      if (logoFile && storage) {
        try {
          const logoRef = storageRef(storage, `partner_logos/${docRef.id}/logo`);
          await uploadBytes(logoRef, logoFile, { contentType: logoFile.type });
          const logoUrl = await getDownloadURL(logoRef);
          const { updateDoc, doc } = await import('firebase/firestore');
          await updateDoc(doc(db, 'schoolPartnerApplications', docRef.id), { logoUrl });
        } catch (uploadErr) {
          console.warn('[partnerApply] Logo upload failed (non-fatal):', uploadErr);
        }
      }
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError('Submission failed. Please try again or email info@muyikaratu.com');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
      <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)' }} onClick={reset} />
      <div className="relative z-10 min-h-full flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-lg rounded-3xl overflow-hidden animate-scale-in"
          style={{ background: 'linear-gradient(135deg,#0b1530,#0f172a)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}
          onClick={e => e.stopPropagation()}
        >
          {/* top accent */}
          <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg,#1B3870,#3D9B3D,#7c3aed)' }} />

          {/* close */}
          <button onClick={reset} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-all z-20">✕</button>

          {/* Header */}
          <div className="px-8 pt-8 pb-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: 'linear-gradient(135deg,#1B3870,#3D9B3D)' }}>🏫</div>
              <div>
                <h2 className="text-xl font-black text-white">Partner with MEF EduSuite</h2>
                <p className="text-slate-400 text-xs">Bring world-class EdTech to your institution</p>
              </div>
            </div>

            {/* step bar */}
            {!submitted && (
              <div className="flex items-center gap-1 mt-5">
                {([1, 2, 3, 4] as Step[]).map((s, i) => (
                  <React.Fragment key={s}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${step === s ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/40' : step > s ? 'bg-green-600 text-white' : 'bg-white/10 text-slate-400'}`}>
                      {step > s ? '✓' : s}
                    </div>
                    {i < 3 && <div className={`flex-1 h-0.5 transition-all ${step > s ? 'bg-green-600' : 'bg-white/10'}`} />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-8 pb-4 max-h-[55vh] overflow-y-auto">
            {submitted ? (
              <div className="text-center py-8 animate-scale-in">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-black text-white mb-2">Application Received!</h3>
                <p className="text-slate-400 text-sm mb-6">
                  Thank you, <strong className="text-white">{form.contactName}</strong>. We have received your partnership application for <strong className="text-blue-400">{form.schoolName}</strong>.
                </p>
                <div className="rounded-2xl p-5 mb-6 text-left space-y-2" style={{ background: 'rgba(27,56,112,0.15)', border: '1px solid rgba(27,56,112,0.35)' }}>
                  <p className="text-blue-400 font-bold text-sm">What happens next?</p>
                  <p className="text-slate-400 text-sm">1. Our team will review your application within 2 business days.</p>
                  <p className="text-slate-400 text-sm">2. You will receive a confirmation email at <strong className="text-white">{form.email}</strong>.</p>
                  <p className="text-slate-400 text-sm">3. Once approved, your portal will be activated immediately.</p>
                  <p className="text-slate-400 text-sm">4. Your first 14 days are free. After that you pay each term from Billing in your dashboard.</p>
                </div>
                <p className="text-slate-500 text-xs mb-4">Questions? Email <span className="text-blue-400">info@muyikaratu.com</span></p>
                <button onClick={reset} className="px-8 py-3 rounded-xl font-black text-white text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg,#1B3870,#3D9B3D)' }}>Close</button>
              </div>
            ) : (
              <div className="space-y-4 py-2">
                {step === 1 && (
                  <>
                    <p className="text-slate-400 text-xs mb-3 font-semibold uppercase tracking-wider">Step 1 · Institution Details</p>
                    <FIELD label="School / Institution Name" value={form.schoolName} onChange={v => set('schoolName', v)} placeholder="e.g. Greenfield Academy" />
                    <SELECT label="Institution Type" value={form.institutionType} onChange={v => set('institutionType', v)}>
                      {INSTITUTION_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </SELECT>
                    {/* Logo upload */}
                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-1.5">School Logo <span className="text-slate-500 font-normal">(optional, max 2 MB)</span></label>
                      <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                      <div className="flex items-center gap-3">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo preview" className="w-14 h-14 rounded-xl object-contain border border-white/10 bg-white/5" />
                        ) : (
                          <div className="w-14 h-14 rounded-xl flex items-center justify-center text-slate-500 text-2xl" style={{ border: '1px dashed rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)' }}>🏫</div>
                        )}
                        <button type="button" onClick={() => logoInputRef.current?.click()}
                          className="px-4 py-2 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
                          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                          {logoFile ? '↺ Change Logo' : 'Upload Logo'}
                        </button>
                        {logoFile && <span className="text-xs text-slate-400 truncate max-w-[120px]">{logoFile.name}</span>}
                      </div>
                    </div>
                    <FIELD label="Address" value={form.address} onChange={v => set('address', v)} placeholder="Full address" required={false} />
                    <div className="grid grid-cols-2 gap-3">
                      <FIELD label="City" value={form.city} onChange={v => set('city', v)} placeholder="e.g. Abuja" />
                      <SELECT label="Country" value={form.country} onChange={v => set('country', v)}>
                        <option>Nigeria</option>
                        <option>United Kingdom</option>
                        <option>Canada</option>
                        <option>United States</option>
                        <option>Ghana</option>
                        <option>Kenya</option>
                        <option>South Africa</option>
                        <option>Other</option>
                      </SELECT>
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <p className="text-slate-400 text-xs mb-3 font-semibold uppercase tracking-wider">Step 2 · Contact Person</p>
                    <FIELD label="Full Name" value={form.contactName} onChange={v => set('contactName', v)} placeholder="Principal / Director name" />
                    <FIELD label="Official Email Address" value={form.email} onChange={v => set('email', v)} placeholder="admin@school.edu.ng" type="email" />
                    <FIELD label="Phone / WhatsApp" value={form.phone} onChange={v => set('phone', v)} placeholder="+234 800 000 0000" type="tel" />
                  </>
                )}

                {step === 3 && (
                  <>
                    <p className="text-slate-400 text-xs mb-3 font-semibold uppercase tracking-wider">Step 3 · Your School</p>
                    <SELECT label="Estimated Number of Students" value={form.estimatedStudents} onChange={v => set('estimatedStudents', v)}>
                      <option value="">— Select range —</option>
                      <option>1–50</option>
                      <option>51–150</option>
                      <option>151–500</option>
                      <option>501–1,000</option>
                      <option>1,000+</option>
                    </SELECT>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-1.5">What challenges are you trying to solve? <span className="text-rose-400">*</span></label>
                      <textarea
                        value={form.currentChallenges}
                        onChange={e => set('currentChallenges', e.target.value)}
                        rows={4}
                        placeholder="e.g. We need a better way to manage CBT exams, track student progress, and communicate with parents..."
                        className="w-full px-4 py-3 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
                      />
                    </div>
                  </>
                )}

                {step === 4 && (
                  <>
                    <p className="text-slate-400 text-xs mb-3 font-semibold uppercase tracking-wider">Step 4 · Choose a plan</p>
                    <p className="text-slate-400 text-xs mb-4">No payment now. Once approved, your first 14 days are free; then you pay per term from Billing. Prices are set in dollars and charged in naira at the term's CBN rate (₦{pricing.appliedRate.toLocaleString()} to $1 now), plus 7.5% VAT.</p>
                    <PlanCards dark rate={pricing.appliedRate} value={form.interestedPlan} onChange={p => setForm(f => ({ ...f, interestedPlan: p }))} only={/Training|Vocational|College|Tutor/.test(form.institutionType) ? ['trn', 'std', 'pre', 'ent'] : ['ess', 'std', 'pre', 'ent']} />
                    {form.interestedPlan === 'ent' && <p className="mt-2 text-xs text-amber-300">Enterprise is priced by agreement. Submit the application and MEF will contact you to agree prices and volumes.</p>}
                    <p className="mt-3 text-xs text-slate-500">{discountSummary(pricing)}</p>
                    <FIELD label="Referral code (optional)" value={form.referralCode} onChange={v => set('referralCode', v)} placeholder="The code of the school that told you about us" required={false} />
                    {error && <p className="text-rose-400 text-sm font-semibold mt-3">{error}</p>}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!submitted && (
            <div className="px-8 py-5 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <button
                onClick={() => step > 1 ? setStep(s => (s - 1) as Step) : reset()}
                className="px-5 py-2.5 rounded-xl font-bold text-sm text-slate-400 hover:text-white hover:bg-white/10 transition-all"
              >
                {step === 1 ? 'Cancel' : '← Back'}
              </button>

              {step < 4 ? (
                <button
                  onClick={() => canNext() && setStep(s => (s + 1) as Step)}
                  disabled={!canNext()}
                  className="px-6 py-2.5 rounded-xl font-black text-white text-sm transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: 'linear-gradient(135deg,#1B3870,#254d9e)', boxShadow: '0 4px 16px rgba(27,56,112,0.4)' }}
                >
                  Continue →
                </button>
              ) : (
                <button
                  onClick={() => void handleSubmit()}
                  disabled={submitting}
                  className="px-6 py-2.5 rounded-xl font-black text-white text-sm flex items-center gap-2 transition-all hover:scale-105 active:scale-95 disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg,#3D9B3D,#2d7a2d)', boxShadow: '0 4px 16px rgba(61,155,61,0.4)' }}
                >
                  {submitting ? <><Spinner className="w-4 h-4" /> Submitting…</> : 'Submit application'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
